/**
 * マニュアル: Genemo Post の使い方の流れと、投稿先（X・Threads・Instagram・WordPress）ごとの接続方法。
 * 接続ガイドは /manual#threads のようにハッシュで直接開ける（ペルソナ画面の接続フォームからリンクする）。
 */
import { useEffect, useRef, useState, type ComponentType, type KeyboardEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ArrowRight, ListChecks, PenLine, Plug, Store } from 'lucide-react'
import { MANUAL_TABS, manualTabFromHash, type ManualTab } from '../lib/manualTabs'
import { XGuide } from './manual/XGuide'
import { ThreadsGuide } from './manual/ThreadsGuide'
import { InstagramGuide } from './manual/InstagramGuide'
import { WordPressGuide } from './manual/WordPressGuide'
import { UiLabel } from './manual/parts'

const FLOW = [
  { icon: Store, title: 'ペルソナを作る', body: '「誰として投稿するか」を決めます。店名と話し方を入れます。', to: '/personas' },
  { icon: Plug, title: '投稿先を接続する', body: 'ペルソナに X・Threads・Instagram をつなぎます。下のガイドを見ながら進めてください。', to: '/personas' },
  { icon: PenLine, title: '投稿を作る', body: 'お店の情報と投稿先を選ぶと、AI が本文と画像を作ります。', to: '/compose' },
  { icon: ListChecks, title: '承認して投稿', body: '承認キューで中身を確かめて承認すると、すぐ、または予約した時間に投稿されます。', to: '/queue' },
] as const

/** 投稿先の比較（何を貼るか・期限・画像・費用） */
const COMPARE: Array<{ key: ManualTab; name: string; paste: string; expiry: string; image: string; cost: string; time: string; note?: string }> = [
  { key: 'x', name: 'X', paste: 'キー4つ', expiry: 'なし', image: 'あれば添付', cost: 'X の API の条件による', time: '15分' },
  { key: 'threads', name: 'Threads', paste: '長期トークン1つ', expiry: '60日（自動で延長）', image: 'あれば添付', cost: '無料', time: '20分' },
  { key: 'instagram', name: 'Instagram', paste: '長期トークン1つ', expiry: '60日（自動で延長）', image: '必須', cost: '無料', time: '20分' },
  { key: 'wordpress', name: 'WordPress', paste: 'URL・ユーザー名・専用パスワード', expiry: 'なし', image: '—', cost: '無料', time: '5分', note: '準備中（今は接続できません）' },
]

const GUIDES: Record<ManualTab, ComponentType> = {
  x: XGuide, threads: ThreadsGuide, instagram: InstagramGuide, wordpress: WordPressGuide,
}

export function Manual() {
  const location = useLocation()
  const navigate = useNavigate()
  const [tab, setTab] = useState<ManualTab>(() => manualTabFromHash(location.hash))
  const guideTop = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<Partial<Record<ManualTab, HTMLButtonElement | null>>>({})

  // ハッシュつきで開かれたら（ペルソナ画面のリンクなど）、そのガイドを選んで見出しまで送る
  useEffect(() => {
    if (!location.hash) return
    setTab(manualTabFromHash(location.hash))
    guideTop.current?.scrollIntoView({ block: 'start' })
  }, [location.hash])

  const select = (key: ManualTab, focus = false) => {
    setTab(key)
    navigate({ hash: key }, { replace: true })
    if (focus) tabRefs.current[key]?.focus()
  }

  // タブの左右キー移動（WAI-ARIA の tabs パターン）
  const onTabKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    const i = MANUAL_TABS.findIndex(t => t.key === tab)
    const next = e.key === 'ArrowRight' ? (i + 1) % MANUAL_TABS.length
      : e.key === 'ArrowLeft' ? (i - 1 + MANUAL_TABS.length) % MANUAL_TABS.length
        : e.key === 'Home' ? 0 : e.key === 'End' ? MANUAL_TABS.length - 1 : -1
    if (next < 0) return
    e.preventDefault()
    select(MANUAL_TABS[next].key, true)
  }

  /** 比べ方の表から、そのガイドを選んで見出しまで送る */
  const showGuide = (key: ManualTab) => {
    select(key)
    guideTop.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }

  const Guide = GUIDES[tab]

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold leading-snug">マニュアル</h1>
        <p className="text-sm text-ink-muted">Genemo Post の使い方の流れと、投稿先ごとの接続のしかたです。</p>
      </div>

      <section aria-labelledby="flow-heading" className="space-y-3">
        <h2 id="flow-heading" className="text-lg font-bold">使い方の流れ</h2>
        <ol className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {FLOW.map(({ icon: Icon, title, body, to }, i) => (
            <li key={title} className="gm-card relative flex flex-col gap-2 p-4">
              <div className="flex items-center gap-2">
                <span className="gm-num flex h-7 w-7 items-center justify-center rounded-full bg-primary-subtle text-sm font-bold text-primary">{i + 1}</span>
                <Icon size={18} aria-hidden className="text-primary" />
                <h3 className="text-sm font-bold">{title}</h3>
              </div>
              <p className="flex-1 text-sm leading-relaxed text-ink-muted">{body}</p>
              <Link to={to} className="inline-flex items-center gap-1 text-sm font-bold text-primary hover:underline">
                開く <ArrowRight size={14} aria-hidden />
              </Link>
            </li>
          ))}
        </ol>
      </section>

      <section aria-labelledby="compare-heading" className="space-y-3">
        <h2 id="compare-heading" className="text-lg font-bold">投稿先の比べ方</h2>
        {/* 狭い画面は表をやめて、投稿先ごとのカードにする（横スクロールさせない） */}
        <ul className="grid gap-2 md:hidden">
          {COMPARE.map(r => (
            <li key={r.key} className="gm-card p-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-bold">{r.name}</span>
                {r.note && <span className="text-xs text-warn">{r.note}</span>}
                <button type="button" onClick={() => showGuide(r.key)} className="gm-btn gm-btn-ghost gm-btn-sm ml-auto">手順を見る</button>
              </div>
              <dl className="mt-1.5 grid grid-cols-[4.5em_minmax(0,1fr)] gap-x-2 gap-y-0.5 text-[13px]">
                <dt className="text-ink-muted">貼るもの</dt><dd>{r.paste}</dd>
                <dt className="text-ink-muted">期限</dt><dd>{r.expiry}</dd>
                <dt className="text-ink-muted">画像</dt><dd>{r.image}</dd>
                <dt className="text-ink-muted">費用</dt><dd>{r.cost}</dd>
                <dt className="text-ink-muted">目安</dt><dd>{r.time}</dd>
              </dl>
            </li>
          ))}
        </ul>
        <div className="gm-card relative hidden overflow-x-auto md:block">
          <table className="gm-table w-full text-sm">
            <thead>
              <tr>
                <th scope="col" className="text-left">投稿先</th>
                <th scope="col" className="text-left">貼るもの</th>
                <th scope="col" className="text-left">期限</th>
                <th scope="col" className="text-left">画像</th>
                <th scope="col" className="text-left">費用</th>
                <th scope="col" className="text-left">目安</th>
                <th scope="col"><span className="sr-only">ガイド</span></th>
              </tr>
            </thead>
            <tbody>
              {COMPARE.map(r => (
                <tr key={r.key}>
                  <th scope="row" className="text-left font-bold">
                    {r.name}
                    {r.note && <span className="mt-0.5 block text-xs font-normal text-warn">{r.note}</span>}
                  </th>
                  <td>{r.paste}</td>
                  <td>{r.expiry}</td>
                  <td>{r.image === '必須' ? <span className="gm-chip gm-chip-warn">必須</span> : r.image}</td>
                  <td>{r.cost}</td>
                  <td className="gm-num">{r.time}</td>
                  <td className="text-right">
                    <button type="button" onClick={() => showGuide(r.key)}
                      className="gm-btn gm-btn-ghost gm-btn-sm whitespace-nowrap">手順を見る</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-ink-muted">
          同じペルソナに複数の投稿先をつなげます。1つの投稿を作ると、つないだ投稿先それぞれに出せます。
          接続はペルソナ画面の各ボタン（例: <UiLabel>Threads を接続（トークン）</UiLabel>）から行います。
        </p>
      </section>

      <section aria-labelledby="guide-heading" className="space-y-4">
        <div ref={guideTop} className="scroll-mt-4" />
        <h2 id="guide-heading" className="text-lg font-bold">接続ガイド</h2>
        <div role="tablist" aria-label="投稿先" className="flex gap-1 overflow-x-auto overflow-y-hidden border-b border-line">
          {MANUAL_TABS.map(t => {
            const active = t.key === tab
            return (
              <button key={t.key} ref={el => { tabRefs.current[t.key] = el }}
                type="button" role="tab" id={`manual-tab-${t.key}`} aria-selected={active} aria-controls={`manual-panel-${t.key}`}
                tabIndex={active ? 0 : -1} onClick={() => select(t.key)} onKeyDown={onTabKey}
                className={`-mb-px min-h-11 whitespace-nowrap border-b-2 px-4 text-sm transition-colors ${
                  active ? 'border-primary font-bold text-ink' : 'border-transparent text-ink-muted hover:text-ink'
                }`}>
                {t.label}
              </button>
            )
          })}
        </div>
        <div role="tabpanel" id={`manual-panel-${tab}`} aria-labelledby={`manual-tab-${tab}`} tabIndex={0} className="focus:outline-none">
          <Guide />
        </div>
      </section>
    </div>
  )
}
